import { useState } from 'react';
import {
  RefreshCw,
  Database,
  Layers,
  Building2,
  CheckCircle2,
  AlertTriangle,
  FileCode,
  ArrowUpRight,
  Clock,
  Play,
  Upload,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import { Textarea } from '../../components/ui/Textarea.jsx';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table.jsx';
import { formatDate } from '../../lib/utils.js';

/**
 * Central truth predicate for import run success.
 * An import run is successful ONLY if status === 'completed' AND itemsFailed is an explicit numeric safe integer equal to 0.
 */
export function isImportSuccess(run) {
  if (!run || typeof run !== 'object') return false;
  return (
    run.status === 'completed' &&
    typeof run.itemsFailed === 'number' &&
    Number.isSafeInteger(run.itemsFailed) &&
    run.itemsFailed === 0
  );
}

export function getImportStatusMeta(run) {
  if (!run || typeof run !== 'object') {
    return { label: 'Unbekannt', variant: 'secondary', isSuccess: false };
  }
  if (isImportSuccess(run)) {
    return { label: 'Erfolgreich', variant: 'success', isSuccess: true };
  }
  if (
    run.status === 'completed' &&
    typeof run.itemsFailed === 'number' &&
    Number.isSafeInteger(run.itemsFailed) &&
    run.itemsFailed > 0
  ) {
    return { label: 'Teilweise fehlgeschlagen', variant: 'destructive', isSuccess: false };
  }
  return { label: 'Fehlgeschlagen', variant: 'destructive', isSuccess: false };
}

export function CatalogSyncTab({
  tenants = [],
  onTriggerSync,
  syncing = false,
  initialImportRun = null,
  initialHistory = [],
}) {
  const [lastImportRun, setLastImportRun] = useState(initialImportRun);
  const [syncError, setSyncError] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customJsonPayload, setCustomJsonPayload] = useState('');

  // Initialized with genuine empty history or initial history
  const [importHistory, setImportHistory] = useState(initialHistory);

  const handleCustomSync = async () => {
    try {
      setSyncError(null);
      let payload;
      try {
        payload = JSON.parse(customJsonPayload);
      } catch (parseErr) {
        setSyncError('Ungültiges JSON-Format. Bitte prüfen Sie Ihre Eingabe.');
        return;
      }

      const res = await onTriggerSync(payload);
      if (res?.importRun) {
        setLastImportRun(res.importRun);
        setImportHistory((prev) => [res.importRun, ...prev]);
      }
      setShowCustomModal(false);
      setCustomJsonPayload('');
    } catch (err) {
      setSyncError(err.message || 'Fehler beim Ausführen des benutzerdefinierten Imports');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 border-b border-[var(--border)] pb-4">
        <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
          ASL Master-Katalog Synchronisation
        </h2>
        <p className="text-xs text-[var(--secondary)]">
          Überwachung des ASL-Produktkatalogs in Cloudflare D1 und Ausführung von Batch-Importen.
        </p>
      </div>

      {/* Catalog System & Status Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="border-[var(--border)] bg-white shadow-xs">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-[var(--secondary)] uppercase tracking-wider">
              Partner-Shops
            </CardTitle>
            <Building2 className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{tenants.length}</div>
            <p className="text-xs text-[var(--secondary)] mt-1">Registrierte Mandanten</p>
          </CardContent>
        </Card>

        <Card className="border-[var(--border)] bg-white shadow-xs">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-[var(--secondary)] uppercase tracking-wider">
              D1 Datenbank
            </CardTitle>
            <Database className="h-4 w-4 text-[var(--primary)]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[var(--foreground)]">Cloudflare D1</div>
            <p className="text-xs text-[var(--secondary)] mt-1">Status: Ungeprüft (Session-gebunden)</p>
          </CardContent>
        </Card>

        <Card className="border-[var(--border)] bg-white shadow-xs">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold text-[var(--secondary)] uppercase tracking-wider">
              Import-Schnittstelle
            </CardTitle>
            <Layers className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-indigo-600">REST API</div>
            <p className="text-xs text-[var(--secondary)] mt-1">POST /api/admin/catalog/import (Session-gebunden)</p>
          </CardContent>
        </Card>
      </div>

      {/* Sync Execution Section */}
      <Card className="border-[var(--border)] bg-white shadow-xs">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 text-[var(--primary)] ${syncing ? 'animate-spin' : ''}`} />
              <CardTitle className="text-sm font-bold">Katalog-Import & Synchronisationslauf</CardTitle>
            </div>
            <Badge variant="outline" className="text-xs font-mono">
              POST /api/admin/catalog/import
            </Badge>
          </div>
          <CardDescription className="text-xs">
            Führt Ingestion von Kategorien und Produkten in atomaren Batches auf Cloudflare D1 aus.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {syncError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Synchronisationsfehler</AlertTitle>
              <AlertDescription>{syncError}</AlertDescription>
            </Alert>
          )}

          {lastImportRun && isImportSuccess(lastImportRun) && (
            <Alert variant="success" className="border-emerald-300 bg-emerald-50 text-emerald-800">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle>Katalog-Import erfolgreich abgeschlossen!</AlertTitle>
              <AlertDescription className="text-xs space-y-1 mt-1">
                <div>Import-Run ID: <code className="font-mono">{lastImportRun.id}</code></div>
                <div className="font-semibold text-emerald-900">
                  {`${lastImportRun.itemsCreated || 0} Artikel neu angelegt • ${lastImportRun.itemsUpdated || 0} aktualisiert • 0 Fehler`}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {lastImportRun && !isImportSuccess(lastImportRun) && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {lastImportRun.status === 'completed' &&
                typeof lastImportRun.itemsFailed === 'number' &&
                Number.isSafeInteger(lastImportRun.itemsFailed) &&
                lastImportRun.itemsFailed > 0
                  ? 'Katalog-Import teilweise fehlgeschlagen'
                  : 'Katalog-Import fehlgeschlagen'}
              </AlertTitle>
              <AlertDescription className="text-xs space-y-1 mt-1">
                <div>Import-Run ID: <code className="font-mono">{lastImportRun.id}</code></div>
                <div className="font-semibold">
                  {`${lastImportRun.itemsCreated || 0} Artikel angelegt • ${lastImportRun.itemsUpdated || 0} aktualisiert • ${typeof lastImportRun.itemsFailed === 'number' && Number.isSafeInteger(lastImportRun.itemsFailed) ? lastImportRun.itemsFailed : (lastImportRun.itemsFailed ?? 'unbekannt')} Fehler`}
                </div>
                {Array.isArray(lastImportRun.errors) && lastImportRun.errors.length > 0 && (
                  <ul className="list-disc pl-4 text-[11px] mt-1.5 space-y-0.5 max-h-32 overflow-y-auto">
                    {lastImportRun.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        {typeof err === 'string'
                          ? err
                          : (err.sku ? `SKU ${err.sku}: ${err.error || err.message || 'Validierungsfehler'}` : (err.error || err.message || JSON.stringify(err)))}
                      </li>
                    ))}
                    {lastImportRun.errors.length > 5 && (
                      <li className="italic">... und {lastImportRun.errors.length - 5} weitere Fehler</li>
                    )}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              disabled
              title="Funktion in aktuellem Release nicht angebunden"
              className="gap-2 opacity-60 cursor-not-allowed"
            >
              <Play className="h-4 w-4" />
              <span>Katalog-Synchronisation (Funktion in aktuellem Release nicht angebunden)</span>
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowCustomModal(true)}
              disabled={syncing}
              className="gap-2 cursor-pointer"
            >
              <Upload className="h-4 w-4" />
              Benutzerdefiniertes JSON importieren
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History Log Table */}
      <Card className="border-[var(--border)] bg-white shadow-xs overflow-hidden">
        <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Sitzungs-Historie (In-Memory)
            </CardTitle>
          </div>
          <CardDescription className="text-xs text-slate-500">
            Zeigt in dieser Browsersitzung durchgeführte Importläufe. Persistente D1-Audit-Historie ist in diesem Release nicht angebunden.
          </CardDescription>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="font-semibold text-xs">Run-ID</TableHead>
                <TableHead className="font-semibold text-xs">Zeitpunkt</TableHead>
                <TableHead className="font-semibold text-xs">Quelle</TableHead>
                <TableHead className="font-semibold text-xs text-right">Gesamt</TableHead>
                <TableHead className="font-semibold text-xs text-right">Neu</TableHead>
                <TableHead className="font-semibold text-xs text-right">Aktualisiert</TableHead>
                <TableHead className="font-semibold text-xs text-right">Fehler</TableHead>
                <TableHead className="font-semibold text-xs text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {importHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-slate-500">
                    Keine Synchronisationsläufe in dieser Sitzung aufgezeichnet.
                  </TableCell>
                </TableRow>
              ) : (
                importHistory.map((run) => (
                  <TableRow key={run.id} className="hover:bg-slate-50/60 transition-colors">
                    <TableCell className="font-mono text-xs font-bold text-slate-800">
                      {run.id}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {formatDate(run.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {run.sourceType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">
                      {run.itemsTotal}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-emerald-600 font-semibold">
                      +{run.itemsCreated}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-blue-600 font-semibold">
                      {run.itemsUpdated}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-rose-600 font-semibold">
                      {typeof run.itemsFailed === 'number' && Number.isSafeInteger(run.itemsFailed) ? run.itemsFailed : (run.itemsFailed ?? '—')}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const statusMeta = getImportStatusMeta(run);
                        return (
                          <Badge
                            variant={statusMeta.variant}
                            className="text-[10px]"
                          >
                            {statusMeta.label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Custom JSON Import Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl border border-[var(--border)] p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
              <div className="flex items-center gap-2">
                <FileCode className="h-5 w-5 text-[var(--primary)]" />
                <h3 className="font-display font-bold text-base text-slate-900">
                  Benutzerdefiniertes Katalog-JSON importieren
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomModal(false)}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                JSON Payload ({`{ sourceType, categories, products }`})
              </label>
              <Textarea
                value={customJsonPayload}
                onChange={(e) => setCustomJsonPayload(e.target.value)}
                placeholder='{ "sourceType": "manual_json", "categories": [...], "products": [...] }'
                rows={8}
                className="font-mono text-xs"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border)]">
              <Button variant="outline" size="sm" onClick={() => setShowCustomModal(false)}>
                Abbrechen
              </Button>
              <Button size="sm" onClick={handleCustomSync} disabled={syncing}>
                Import ausführen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
